import axios from "axios";
import { fmtDim } from "./dims";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const computeCurrentFY = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return m >= 4 ? `${y}-${String(y + 1).slice(-2)}` : `${y - 1}-${String(y).slice(-2)}`;
};

let _fy = null;
export const setFiscalYear = (fy) => { _fy = fy; };

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (method === "get" && _fy && config.params?.fy === undefined) {
    config.params = { ...(config.params || {}), fy: _fy };
  }
  if (config.params?.fy === "all") delete config.params.fy;  // explicit "all fiscal years"
  return config;
});

// Global API error feedback: every failed call surfaces a toast (deduped by message). Pass { silent: true } to opt out.
export const errorMessage = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join("; ");
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return d.message;
  if (e?.code === "ERR_NETWORK") return "Cannot reach the server — check your connection";
  return e?.message || "Request failed";
};
api.interceptors.response.use((r) => r, async (e) => {
  const status = e?.response?.status;
  const d = e?.response?.data?.detail;
  // Protected field changed by an admin: ask for the reason once and retry the same request with it.
  if (status === 400 && d && typeof d === "object" && d.code === "reason_required" && !e.config?._reasonAsked) {
    const { askReason } = await import("./reason");
    const reason = await askReason(d.fields, d.message);
    if (reason) return api.request({ ...e.config, _reasonAsked: true, headers: { ...(e.config.headers || {}), "X-Change-Reason": reason } });
    return Promise.reject(Object.assign(e, { config: { ...e.config, silent: true } }));
  }
  if (!e?.config?.silent && status !== 401 && !(status === 404 && (e.config?.method || "get") === "get")) {
    import("sonner").then(({ toast }) => toast.error(errorMessage(e), { id: `api-${errorMessage(e)}` }));
  }
  return Promise.reject(e);
});

export const fmt = (n) => {
  if (n === null || n === undefined || n === "") return "-";
  const num = Number(n);
  if (Number.isNaN(num)) return n;
  return num.toLocaleString("en-IN", { maximumFractionDigits: 1 });
};

// Weight (kg): always 2 decimals — 1,234.50 (user rule).
export const kg2 = (n) => {
  if (n === null || n === undefined || n === "") return "-";
  const num = Number(n);
  if (Number.isNaN(num)) return n;
  return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Dimension (width / T1 / T2 / CR, mm): at least 2 decimals, a 3rd kept when present — 12.00, 6.50, 9.525 (user rule).
export const dim2 = fmtDim;
// Work order reference shown everywhere: C1131-A1 (new series) / CR1115-001 (legacy).
export const woRef = (wo, batch) => { const w = String(wo || "").trim(), b = String(batch || "").trim(); return w && b ? `${w}-${b}` : w; };

export const fmtDate = (v) => {
  if (!v) return "-";
  const s = String(v).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return v;
  return `${m[3]}/${m[2]}/${m[1].slice(2)}`;
};

export const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
export const MACHINES = ["C1", "C2", "C3"];
export const SIZES = [8, 12.5, 16, 20, 25];
// Show only the first name (or full email if a bare email) for created/updated/approver display.
export const firstName = (n) => {
  if (!n) return "";
  const s = String(n).trim();
  if (s.includes("@")) return s;
  return s.split(/\s+/)[0];
};

// Today's date in the browser's local timezone as YYYY-MM-DD (toISOString would give UTC).
export const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// dd/mm/yyyy (full year) — used where the year must be unambiguous, e.g. Expected Dispatch.
export const fmtDateFull = fmtDate;

// Timestamp → dd/mm/yy, HH:mm (24-hour, local time). Use for created / approved / updated columns everywhere.
export const fmtDateTime = (v, { seconds = false } = {}) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}, ${p(d.getHours())}:${p(d.getMinutes())}${seconds ? `:${p(d.getSeconds())}` : ""}`;
};

// Any date-like value (ISO date or timestamp) → dd/mm/yy in local time.
export const fmtDMY = (v) => {
  if (!v) return "-";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return fmtDate(v);
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
};

// Generic cell text: ISO dates → dd/mm/yy, ISO datetimes → dd/mm/yy HH:MM, everything else untouched.
const ISO_DT = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;
export const fmtCell = (v) => {
  if (v == null) return "";
  if (typeof v !== "string") return String(v);
  const m = v.match(ISO_DT);
  if (!m || (v.length !== 10 && !/^\d{4}-\d{2}-\d{2}[T ]/.test(v))) return v;
  return `${m[3]}/${m[2]}/${m[1].slice(2)}${m[4] ? ` ${m[4]}:${m[5]}` : ""}`;
};
